import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { apiClient } from "@/lib/apiClient";
import { Tickets, Clock } from "@/components/icons";
import styles from "./SupportSlaReportPage.module.scss";

// ---------- Types (local — kept in-file to avoid touching shared services) ----------
type SupportSlaStatus = "OPEN" | "IN_REVIEW" | "RESOLVED" | "CLOSED" | "OTHER";

interface SupportSlaItem {
  ticketID: string | number;
  requestNo: string;
  subject: string;
  senderName: string;
  educationTitle: string | null;
  status: SupportSlaStatus;
  createdAt: string;
  resolvedAt: string | null;
  resolutionHours: number | null;
  isWithinSla: boolean | null;
}

interface SupportSlaMonthlyPoint {
  month: string;
  opened: number;
  resolved: number;
}

interface SupportSlaSummary {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  avgResolutionHours: number;
  slaCompliancePercent: number;
  overdueCount: number;
}

interface SupportSlaResponse {
  summary: SupportSlaSummary;
  byStatus: { status: SupportSlaStatus; count: number }[];
  byEducation: { educationTitle: string; count: number }[];
  resolutionBuckets: { label: string; count: number }[];
  monthlyTrend: SupportSlaMonthlyPoint[];
  items: SupportSlaItem[];
  count: number;
}

interface SupportSlaParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ---------- Local fetcher ----------
const fetchSupportSla = async (
  params: SupportSlaParams,
  signal: AbortSignal,
): Promise<SupportSlaResponse> => {
  const { data } = await apiClient.get(
    "/api/admin/reports/support-sla",
    { params, signal },
  );
  const body = data as { data?: SupportSlaResponse } | SupportSlaResponse;
  if (body && typeof body === "object" && "data" in body && body.data) {
    return body.data;
  }
  return body as SupportSlaResponse;
};

// ---------- Helpers ----------
const PAGE_SIZE = 20;

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "—";
  }
};

const formatHours = (h: number | null) =>
  h == null ? "—" : `${h.toFixed(1)} sa`;

const formatPercent = (n: number) => `${n.toFixed(1)}%`;

const STATUS_LABELS: Record<SupportSlaStatus, string> = {
  OPEN: "Açık",
  IN_REVIEW: "İncelemede",
  RESOLVED: "Çözüldü",
  CLOSED: "Kapatıldı",
  OTHER: "Diğer",
};

const STATUS_COLORS: Record<SupportSlaStatus, string> = {
  OPEN: "#ef4444",
  IN_REVIEW: "#f59e0b",
  RESOLVED: "#10b981",
  CLOSED: "#64748b",
  OTHER: "#94a3b8",
};

const SupportSlaReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: SupportSlaParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    SupportSlaResponse,
    SupportSlaParams
  >({
    fetcher: (p, signal) => fetchSupportSla(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const monthlyTrend = data?.monthlyTrend ?? [];
  const byStatus = data?.byStatus ?? [];
  const resolutionBuckets = data?.resolutionBuckets ?? [];

  // ---------- Columns ----------
  const columns: TableColumn<SupportSlaItem>[] = [
    {
      header: t("reports.supportSla.headerRequestNo", "TALEP NO"),
      accessor: (row) => (
        <span className={styles.requestNoCell}>{row.requestNo}</span>
      ),
    },
    {
      header: t("reports.supportSla.headerSubject", "KONU"),
      accessor: (row) => (
        <span className={styles.subjectCell} title={row.subject}>
          {row.subject}
        </span>
      ),
    },
    {
      header: t("reports.supportSla.headerSender", "GÖNDEREN"),
      accessor: (row) => row.senderName,
    },
    {
      header: t("reports.supportSla.headerEducation", "EĞİTİM"),
      accessor: (row) => row.educationTitle ?? "—",
    },
    {
      header: t("reports.supportSla.headerStatus", "DURUM"),
      accessor: (row) => (
        <span
          className={styles.statusBadge}
          style={{
            background: `${STATUS_COLORS[row.status]}1a`,
            color: STATUS_COLORS[row.status],
          }}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      header: t("reports.supportSla.headerCreatedAt", "AÇILMA"),
      accessor: (row) => formatDate(row.createdAt),
    },
    {
      header: t("reports.supportSla.headerResolutionHours", "ÇÖZÜM SAAT"),
      accessor: (row) => formatHours(row.resolutionHours),
    },
    {
      header: t("reports.supportSla.headerSla", "SLA"),
      accessor: (row) => {
        if (row.isWithinSla == null) {
          return <span className={styles.slaNeutral}>—</span>;
        }
        return row.isWithinSla ? (
          <span className={styles.slaGood}>OK</span>
        ) : (
          <span className={styles.slaBad}>İHLAL</span>
        );
      },
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.supportSla.title", "Destek Talebi SLA")}
      categoryLabel={t(
        "reportCatalog.categories.engagement.title",
        "Etkileşim & Aktivite",
      )}
      description={t(
        "reports.supportSla.description",
        "Destek talebi SLA uyumu, çözüm süreleri ve geciken ticket analizleri.",
      )}
      exportSlot={
        <ReportExportButtons
          reportType="SUPPORT_SLA"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="destek-sla"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      {/* ---------- KPI ---------- */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.supportSla.kpiOpen", "Açık Ticket")}
          value={summary?.openTickets ?? "—"}
          icon={<Tickets width={18} height={18} />}
          accent="warning"
        />
        <ReportKPICard
          title={t("reports.supportSla.kpiAvgResolution", "Ortalama Çözüm Saat")}
          value={summary ? formatHours(summary.avgResolutionHours) : "—"}
          icon={<Clock width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.supportSla.kpiSlaCompliance", "SLA Uyum %")}
          value={summary ? formatPercent(summary.slaCompliancePercent) : "—"}
          icon={<Clock width={18} height={18} />}
          accent={
            summary && summary.slaCompliancePercent >= 90
              ? "success"
              : summary && summary.slaCompliancePercent >= 70
                ? "warning"
                : "danger"
          }
        />
        <ReportKPICard
          title={t("reports.supportSla.kpiOverdue", "Geciken Ticket")}
          value={summary?.overdueCount ?? "—"}
          icon={<Tickets width={18} height={18} />}
          accent={
            summary && summary.overdueCount > 0 ? "danger" : "success"
          }
        />
      </div>

      {/* ---------- Charts ---------- */}
      <div className={styles.chartGrid}>
        <ReportChart
          title={t(
            "reports.supportSla.chartMonthly",
            "Açılan vs Çözülen (Aylık)",
          )}
          type="line"
          isLoading={isLoading}
          isEmpty={monthlyTrend.length === 0}
          series={[
            {
              name: t("reports.supportSla.opened", "Açılan"),
              data: monthlyTrend.map((p) => p.opened),
            },
            {
              name: t("reports.supportSla.resolved", "Çözülen"),
              data: monthlyTrend.map((p) => p.resolved),
            },
          ]}
          options={{
            xaxis: { categories: monthlyTrend.map((p) => p.month) },
            colors: ["#4f46e5", "#10b981"],
            stroke: { curve: "smooth", width: 3 },
            markers: { size: 4 },
          }}
        />

        <ReportChart
          title={t("reports.supportSla.chartStatus", "Durum Dağılımı")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byStatus.length === 0}
          series={byStatus.map((s) => s.count)}
          options={{
            labels: byStatus.map(
              (s) => STATUS_LABELS[s.status] ?? s.status,
            ),
            colors: byStatus.map(
              (s) => STATUS_COLORS[s.status] ?? "#94a3b8",
            ),
            legend: { position: "bottom" },
          }}
        />

        <ReportChart
          title={t(
            "reports.supportSla.chartBuckets",
            "Çözüm Süresi Dağılımı",
          )}
          type="bar"
          isLoading={isLoading}
          isEmpty={resolutionBuckets.length === 0}
          series={[
            {
              name: t("reports.supportSla.tickets", "Ticket"),
              data: resolutionBuckets.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: resolutionBuckets.map((b) => b.label) },
            colors: ["#0ea5e9"],
            plotOptions: {
              bar: { columnWidth: "45%", borderRadius: 4 },
            },
          }}
        />
      </div>

      {/* ---------- Table ---------- */}
      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.ticketID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.supportSla.noData", "Veri yüklenemedi.")
            : t("reports.supportSla.noTickets", "Destek talebi bulunamadı.")
        }
      />
    </ReportDetailLayout>
  );
};

export default SupportSlaReportPage;
